import {useCallback, useRef, useState} from "react";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {withRetry} from "./retry";
import {
  AmplifyClientKey,
  GraphqlResult,
  GraphqlVariables,
  LazyQueryTrigger,
  toError
} from "./types";

interface UseGraphqlLazyQueryOptions<
  _TData,
  TVariables extends GraphqlVariables
> {
  /** Amplify query string */
  amplifyQuery: string;
  /** Default variables (can be overridden at call time) */
  variables?: TVariables;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
}

/**
 * Amplify-only lazy query hook returning [triggerFn, { data, loading, error }].
 */
export function useGraphqlLazyQuery<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(
  options: UseGraphqlLazyQueryOptions<TData, TVariables>
): [LazyQueryTrigger<TData, TVariables>, GraphqlResult<TData>] {
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  const [amplifyData, setAmplifyData] = useState<TData | undefined>(undefined);
  const [amplifyLoading, setAmplifyLoading] = useState(false);
  const [amplifyError, setAmplifyError] = useState<Error | undefined>(
    undefined
  );
  const cancelledRef = useRef(false);
  // Tracks the latest trigger call so earlier in-flight requests don't overwrite state
  const callIdRef = useRef(0);

  const amplifyTrigger: LazyQueryTrigger<TData, TVariables> = useCallback(
    async (triggerOptions?) => {
      const vars = triggerOptions?.variables ?? options.variables;
      const thisCallId = ++callIdRef.current;
      cancelledRef.current = false;
      setAmplifyLoading(true);
      setAmplifyError(undefined);

      try {
        if (!client) {
          throw new Error("Amplify client not initialized");
        }
        const result = await withRetry(() =>
          client.graphql<GraphQLQuery<TData>>({
            query: options.amplifyQuery,

            variables: vars as any
          })
        );
        const data = result.data as TData;

        if (!cancelledRef.current && callIdRef.current === thisCallId) {
          setAmplifyData(data);
          setAmplifyLoading(false);
        }
        return {data};
      } catch (err) {
        const error = toError(err);
        if (!cancelledRef.current && callIdRef.current === thisCallId) {
          setAmplifyError(error);
          setAmplifyLoading(false);
        }
        throw error;
      }
    },

    [client, options.amplifyQuery]
  );

  return [
    amplifyTrigger,
    {data: amplifyData, loading: amplifyLoading, error: amplifyError}
  ];
}
